import { SPELLS } from '@shared/rules/data/spells'
import type { ImportedCharacter } from './importSheet'
import { splitList, splitNamedText } from './importSheet'

const SPELL_BY_NAME = new Map(SPELLS.map((spell) => [normalise(spell.name), spell]))

function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

export interface SpellMatch {
  cantrips: string[]
  spells: string[]
  /** Names we could not resolve to a known spell, kept for the notes field. */
  unmatched: string[]
}

/**
 * Spell names on a sheet are free text; the app stores ids. Anything we cannot
 * resolve is handed back so it can be preserved as a note rather than dropped.
 */
export function matchSpells(names: string[]): SpellMatch {
  const cantrips: string[] = []
  const spells: string[] = []
  const unmatched: string[] = []

  for (const name of names) {
    const spell = SPELL_BY_NAME.get(normalise(name))
    if (!spell) { unmatched.push(name.trim()); continue }
    if (spell.level === 0) cantrips.push(spell.id)
    else spells.push(spell.id)
  }
  return { cantrips, spells, unmatched }
}

/** Shapes a reviewed import into exactly what the API expects. */
export function toImportPayload(
  character: ImportedCharacter,
  userId: string | null | undefined,
): Record<string, unknown> {
  const matched = matchSpells(character.spellNames)

  const notes = matched.unmatched.length
    ? `Spells from the imported sheet that are not in the rules database yet:\n${matched.unmatched.join(', ')}`
    : ''

  return {
    name: character.name,
    className: character.className,
    subclass: character.subclass,
    species: character.species,
    background: character.background,
    level: character.level,
    alignment: character.alignment,
    xp: character.xp,
    scores: character.scores,
    hpMax: Math.max(1, character.hpMax),
    hpCurrent: character.hpCurrent || Math.max(1, character.hpMax),
    hpTemp: character.hpTemp,
    armorClass: character.armorClass,
    speed: character.speed,
    initiativeBonus: character.initiativeBonus,
    saveProficiencies: character.saveProficiencies,
    skillProficiencies: character.skillProficiencies,
    attacks: character.attacks,
    features: splitNamedText(character.featuresText),
    speciesTraits: splitNamedText(character.speciesTraitsText),
    feats: splitNamedText(character.featsText),
    equipment: character.equipmentText.split('\n').map((l) => l.trim()).filter(Boolean),
    languages: splitList(character.languagesText),
    tools: splitList(character.toolsText),
    weaponTraining: character.weaponTrainingText,
    coins: character.coins,
    spells: { cantrips: matched.cantrips, spells: matched.spells },
    appearance: character.appearance,
    backstory: character.backstory,
    notes,
    ...(userId === undefined ? {} : { userId }),
  }
}
